import React, { useEffect, useMemo, useRef, useState } from "react";
import { countries, formatLocalByGroups } from "../Utility";

type PhoneInputProps = {
  id: string;
  placeholder?: string;
  required?: boolean;
  value?: string;
  onChange: (val: string) => void;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  placeholder,
  required,
  value,
  onChange,
}) => {
  const [selected, setSelected] = useState(countries[0]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const countryOptions = useMemo(
    () =>
      countries
        .map((c) => ({
          code: c.code,
          label: `${c.flag} ${c.code}`,
          data: c,
        }))
        .sort((a, b) => a.data.name.localeCompare(b.data.name)), // Sort countries alphabetically by name
    [],
  );

  const matched =
    typeof value === "string"
      ? countries.find((c) => value.trim().startsWith(c.code))
      : undefined;

  const effective = matched || selected;
  const localRaw = matched
    ? value!.trim().slice(matched.code.length).trim()
    : value || "";
  const localDigits = (localRaw || "").replace(/\D+/g, "");
  const formattedLocal = formatLocalByGroups(
    localDigits,
    (effective as any).groups,
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectCountry = (code: string) => {
    const next =
      countryOptions.find((opt) => opt.code === code)?.data || effective;
    setSelected(next);
    onChange(`${next.code} ${formattedLocal}`);
    setIsOpen(false);
  };

  const currentLabel =
    countryOptions.find((opt) => opt.code === effective.code)?.label ||
    `${(effective as any).flag ?? ""} ${effective.code}`;

  return (
    <div className="phone-input-wrapper">
      <div
        ref={wrapperRef}
        className="phone-input-country list-dropdown-wrapper"
      >
        <button
          type="button"
          className="form-select list-dropdown-multi-trigger"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="list-dropdown-multi-label">{currentLabel}</span>
          <span className="list-dropdown-multi-caret" aria-hidden="true" />
        </button>
        {isOpen && (
          <div className="list-dropdown-multi-menu" role="listbox">
            {countryOptions.map((c) => {
              const isSelected = c.code === effective.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  className={`list-dropdown-multi-option${
                    isSelected ? " list-dropdown-multi-option--selected" : ""
                  }`}
                  onClick={() => handleSelectCountry(c.code)}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="list-dropdown-multi-option-check">
                    {isSelected ? "✓" : ""}
                  </span>
                  <span className="list-dropdown-multi-option-text">
                    {c.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <input
        id={id}
        className="form-input phone-input-main"
        type="tel"
        placeholder={placeholder || "Phone number"}
        required={required}
        value={formattedLocal}
        onChange={(e) => {
          const digits = String(e.target.value || "").replace(/\D+/g, "");
          const nextLocal = formatLocalByGroups(
            digits,
            (effective as any).groups,
          );
          onChange(`${effective.code} ${nextLocal}`);
        }}
      />
    </div>
  );
};

export default PhoneInput;
